import React, { useState, useEffect, useCallback, useRef  } from 'react';
import { getData, getsubdomains } from '../../../services/admin/revenueForms/paypalTransactionsServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

const PaypalTransactions = () => {
  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeoutRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [dropdownData, setDropdownData] = useState({
            subdomains: []
  });
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Debounce Effect
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1); // reset page only when actual search triggered
    }, 600); // 500ms debounce delay

    return () => clearTimeout(debounceTimeoutRef.current);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (subDomainFilter) filters.sub_domain = subDomainFilter;

      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: debouncedSearch,
        filters,
        startDate: startDate || null,
        endDate: endDate || null,
      };

     const response = await getData(params);
     // ✅ Console log the full API response
      console.log('Fetched Data:', response);
      setDataList(response.rows);
      setTotalRows(response.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, debouncedSearch, subDomainFilter, startDate, endDate]);

   // Fetch all dropdown data
  const fetchDropdownData = useCallback(async () => {
    try {
      const [subdomains] = await Promise.all([
        getsubdomains()            
      ]);
  
      setDropdownData({
        subdomains: subdomains
      });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);
  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);
    
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      case 'GBP': return '£';
      case 'RUB': return '₽';
      case 'THB': return '฿';
      default: return currencyCode || '';
    }
  };

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * rowsPerPage + (index + 1),
      sortable: false,
      width: '60px',
    },
    {
      name: 'Order Id',
      selector: (row) => row.order_id,
      sortable: true,
    },
    {
      name: 'Sub Domain',
      selector: (row) => row.sub_domain,
      sortable: true,
    }, 
    {
      name: 'Amount',
      selector: (row) => {
        const currencySymbol = getCurrencySymbol(row.currency);
        return <b className='fs-4'>{currencySymbol} {row.amount}</b>;
      },
      sortable: true,
    },
    {
          name: 'Status',
          selector: (row) => row.status,
          sortable: true,
          cell: (row) => {
            let bgColor = "";
            let textLabel = "";

          switch (row.status) {
          case "CHARGED":
            bgColor = "bg-success";
            textLabel = "Success";
            break;

          case "AUTHORIZATION_FAILED":
          case "AUTHENTICATION_FAILED":
            bgColor = "bg-danger";
            textLabel = "Failed";
            break;

          case "PENDING_VBV":
            bgColor = "bg-warning text-dark";
            textLabel = "Pending";
            break;

          case "JUSPAY_DECLINED":
            bgColor = "bg-secondary";
            textLabel = "Declined";
            break;

          default:
            bgColor = "bg-secondary";
            textLabel = row.status;
        }

        return (
          <span className={`rounded text-center w-50 py-1 text-white ${bgColor}`}>
            {textLabel}
          </span>
        );
      },
    }         
  ];

  // Function to render expandable row details
  const ExpandableRow = ({ data }) => {

    return (
      <div className="p-3 bg-light border">
        <table className="table table-bordered">
          <tbody>
            <tr>
              <td><b>Payer ID:</b></td>
              <td>{data.payer_id }</td>
            </tr> 
            <tr>
              <td><b>Payment ID:</b></td>
              <td>{data.payment_id}</td>
            </tr>
            <tr>
              <td><b>created Date:</b></td>
              <td>{data.createdAt}</td>
            </tr>
            <tr>
              <td><b>Upadted Date:</b></td>
              <td>{data.updatedAt}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />

        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div className="col-md-2">
              <span>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => { setSubDomainFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Subdomains</option>
                {dropdownData.subdomains.map((subdomains) => (
                  <option key={subdomains.sub_domain} value={subdomains.sub_domain}>{subdomains.sub_domain}</option>
                ))}
              </select>
            </div>
           
            <div className="col-md-2">
              <span>Start Date</span>
              <input type="date" className="form-control" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="col-md-2">
              <span>End Date</span>
              <input type="date" className="form-control" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4">Conference Leads / Revenue Forms / Paypal Transactions</p>
                </div>
              </div>
              <DataTable
                columns={columns}
                data={dataList}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                highlightOnHover
                responsive
                striped
                subHeader
                subHeaderComponent={
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                }
                expandableRows
                expandableRowsComponent={ExpandableRow}  // ✅ Adding expandable row
            />
             
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaypalTransactions;
